import { useEffect, useRef, useState } from "react";
import { Send, MapPin, Globe, Clock, Sparkles, UserRound, RefreshCw, Plus, Trash2, Upload, FlaskConical, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const STATUS_COLORS = { open: "bg-sage-light text-sage-700", pending: "bg-clay-100 text-clay-800", resolved: "bg-sand-100 text-charcoal-400", closed: "bg-sand-100 text-charcoal-400" };
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const locLine = (meta) => {
  const a = meta?.approx_location || {};
  return [a.city, a.region, a.country].filter(Boolean).join(", ") || "unknown";
};

function Toggle({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm text-charcoal-700">
      {label}
      <input data-testid={testid} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />
    </label>
  );
}

function SettingsTab({ employees }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/chat-settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-sm text-charcoal-500">Loading…</p>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/chat-settings", s);
      setS(data);
      toast.success("Chat settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const hours = s.business_hours || {};
  const setDay = (d, on) => set("business_hours", { ...hours, [d]: on ? [{ start: "09:00", end: "18:00" }] : null });
  const setDayTime = (d, k, v) => set("business_hours", { ...hours, [d]: [{ ...(hours[d]?.[0] || { start: "09:00", end: "18:00" }), [k]: v }] });

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <h3 className="mb-4 font-medium text-charcoal-800">Behavior</h3>
        <div className="space-y-3">
          <Toggle testid="cs-ai" label="AI answers enabled (bot replies first)" checked={s.ai_enabled} onChange={(v) => set("ai_enabled", v)} />
          <Toggle testid="cs-handoff" label="Human handoff enabled (escalation)" checked={s.handoff_enabled} onChange={(v) => set("handoff_enabled", v)} />
          <label className="block text-sm text-charcoal-600">Escalation keywords (comma separated — instant handoff)
            <input data-testid="cs-keywords" className={`${FIELD} mt-1`} value={(s.escalation_keywords || []).join(", ")}
              onChange={(e) => set("escalation_keywords", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></label>
          <p className="text-[11px] text-charcoal-400 -mt-1">Complaints, refunds/payment disputes, legal and safety terms always escalate — built in, can't be turned off.</p>
          <label className="block text-sm text-charcoal-600">Low-confidence answers before handoff (1 = escalate immediately)
            <input data-testid="cs-failures" type="number" min="1" max="5" className={`${FIELD} mt-1 w-24`} value={s.escalate_after_ai_failures || 1}
              onChange={(e) => set("escalate_after_ai_failures", Math.max(1, Number(e.target.value) || 1))} /></label>
          <label className="block text-sm text-charcoal-600">Escalated chats email inbox
            <input data-testid="cs-esc-email" className={`${FIELD} mt-1`} value={s.escalation_email || ""} onChange={(e) => set("escalation_email", e.target.value)} /></label>
          <label className="block text-sm text-charcoal-600">Auto-assign escalations to
            <select data-testid="cs-esc-employee" className={`${FIELD} mt-1`} value={s.escalation_employee_id || ""} onChange={(e) => set("escalation_employee_id", e.target.value)}>
              <option value="">No auto-assignment (email inbox only)</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select></label>
        </div>
        <h3 className="mb-3 mt-6 font-medium text-charcoal-800">Human availability (business hours)</h3>
        <p className="mb-3 text-xs text-charcoal-500">Outside these hours, escalated visitors are honestly told the team is offline and will follow up — no fake availability. Currently: <b>{s.humans_online_now ? "ONLINE" : "OFFLINE"}</b> ({s.timezone})</p>
        <div className="space-y-2">
          {DAY_NAMES.map((name, d) => {
            const key = String(d);
            const on = !!(hours[key] && hours[key].length);
            return (
              <div key={d} className="flex flex-wrap items-center gap-2 text-sm">
                <label className="flex w-16 items-center gap-2 text-charcoal-700">
                  <input type="checkbox" checked={on} onChange={(e) => setDay(key, e.target.checked)} />{name}
                </label>
                {on ? (
                  <>
                    <input type="time" className={`${FIELD} w-[110px]`} value={hours[key][0].start} onChange={(e) => setDayTime(key, "start", e.target.value)} />
                    <span className="text-charcoal-400">–</span>
                    <input type="time" className={`${FIELD} w-[110px]`} value={hours[key][0].end} onChange={(e) => setDayTime(key, "end", e.target.value)} />
                  </>
                ) : <span className="text-charcoal-400 text-xs">offline</span>}
              </div>
            );
          })}
          <label className="block pt-1 text-sm text-charcoal-600">Timezone
            <input className={`${FIELD} mt-1 w-60`} value={s.timezone || ""} onChange={(e) => set("timezone", e.target.value)} /></label>
        </div>
      </Card>
      <Card>
        <h3 className="mb-4 font-medium text-charcoal-800">Chatbox text</h3>
        <div className="space-y-3">
          {[["greeting", "Greeting (first bot message)"], ["greeting_es", "Greeting in Spanish (shown to Spanish-browsing visitors)"], ["fallback_message", "Fallback message (AI unsure)"],
            ["clarify_message", "Clarify message (retry before handoff)"],
            ["handoff_message", "Handoff message (humans online)"], ["offline_message", "Offline message (humans away)"],
            ["privacy_text", "Privacy / consent text"]].map(([k, label]) => (
            <label key={k} className="block text-sm text-charcoal-600">{label}
              <textarea data-testid={`cs-${k}`} rows={2} className={`${FIELD} mt-1`} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} /></label>
          ))}
        </div>
        <button data-testid="cs-save" onClick={save} disabled={saving}
          className="mt-5 rounded-full bg-sage px-6 py-2 text-sm font-medium text-white hover:bg-sage-hover disabled:opacity-60">
          {saving ? "Saving…" : "Save all chat settings"}
        </button>
      </Card>
    </div>
  );
}

function KnowledgeTab() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ title: "", content: "" });
  const [busy, setBusy] = useState(false);
  const [testQ, setTestQ] = useState("");
  const [testRes, setTestRes] = useState(null);
  const [testing, setTesting] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/admin/chat-kb").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const saveRow = async (row) => {
    try { await api.put(`/admin/chat-kb/${row.id}`, row); toast.success("Entry saved"); load(); }
    catch { toast.error("Could not save"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this knowledge entry?")) return;
    await api.delete(`/admin/chat-kb/${id}`); load();
  };
  const add = async () => {
    if (!draft.title.trim() || !draft.content.trim()) return toast.error("Title and content required");
    try { await api.post("/admin/chat-kb", draft); setDraft({ title: "", content: "" }); toast.success("Entry added"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const rebuild = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/chat-kb/rebuild"); toast.success(`Rebuilt ${data.site_entries} entries from site content (manual entries kept)`); load(); }
    catch { toast.error("Rebuild failed"); }
    finally { setBusy(false); }
  };
  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/chat-kb/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added to the knowledge base");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const runTest = async () => {
    if (!testQ.trim()) return;
    setTesting(true); setTestRes(null);
    try { const { data } = await api.post("/admin/chat-test", { question: testQ }); setTestRes(data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Test failed"); }
    setTesting(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <h3 className="flex items-center gap-2 font-medium text-charcoal-800"><FlaskConical size={16} className="text-terracotta" /> Test the AI</h3>
        <p className="text-xs text-charcoal-500 mt-0.5">Ask a question exactly like a visitor would — see what the bot answers from the current knowledge base (in any language).</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input data-testid="kb-test-input" className={`${FIELD} flex-1 min-w-[220px]`} placeholder="e.g. ¿Cuánto cuesta mudarse a Mérida con mascotas?"
            value={testQ} onChange={(e) => setTestQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runTest()} />
          <button data-testid="kb-test-run" onClick={runTest} disabled={testing}
            className="rounded-full bg-charcoal-800 px-5 py-2 text-sm font-medium text-white hover:bg-charcoal-900 disabled:opacity-60">
            {testing ? "Thinking…" : "Ask"}
          </button>
        </div>
        {testRes && (
          <div data-testid="kb-test-result" className={`mt-3 rounded-xl p-4 text-sm ${testRes.outcome === "answered" ? "bg-sage/10 text-charcoal-800" : "bg-clay-100 text-clay-900"}`}>
            {testRes.outcome === "answered" ? testRes.answer : `Would hand off to a human — ${testRes.reason}`}
          </div>
        )}
      </Card>
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-medium text-charcoal-800">Knowledge source ({rows.length} entries)</h3>
            <p className="text-xs text-charcoal-500">The AI can ONLY answer from active entries below — site-derived + your manual additions and uploads. Higher priority entries win. No guessing.</p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} data-testid="kb-file-input" type="file" onChange={uploadFile} className="hidden" accept=".pdf,.docx,.txt,.md,.csv" />
            <button data-testid="kb-upload" onClick={() => fileRef.current?.click()} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full border border-sand-200 px-4 py-2 text-sm text-charcoal-600 hover:border-terracotta disabled:opacity-60">
              <Upload size={14} /> Upload document
            </button>
            <button data-testid="kb-rebuild" onClick={rebuild} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full border border-sand-200 px-4 py-2 text-sm text-charcoal-600 hover:border-sage disabled:opacity-60">
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Re-sync from site content
            </button>
          </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
          <input data-testid="kb-new-title" className={FIELD} placeholder="New entry title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <input data-testid="kb-new-content" className={FIELD} placeholder="Approved content the AI may use" value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} />
          <button data-testid="kb-add" onClick={add} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-2 text-sm font-medium text-white hover:bg-terracotta-hover"><Plus size={14} /> Add</button>
        </div>
      </Card>
      {rows.map((r) => (
        <Card key={r.id}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <input className={`${FIELD} max-w-md font-medium`} value={r.title} onChange={(e) => setRows(rows.map((x) => x.id === r.id ? { ...x, title: e.target.value } : x))} />
            <div className="flex items-center gap-3">
              <span className={`rounded-full px-2 py-0.5 text-[10px] ${r.source === "site" ? "bg-sage-light text-sage-700" : "bg-clay-100 text-clay-800"}`}>{r.source}</span>
              {r.updated_at && <span className="text-[10px] text-charcoal-400">updated {r.updated_at.slice(0, 10)}</span>}
              <label className="flex items-center gap-1 text-xs text-charcoal-600">priority
                <input type="number" data-testid={`kb-priority-${r.id}`} className={`${FIELD} w-16 px-2 py-1`} value={r.priority || 0}
                  onChange={(e) => setRows(rows.map((x) => x.id === r.id ? { ...x, priority: Number(e.target.value) || 0 } : x))} />
              </label>
              <label className="flex items-center gap-1.5 text-xs text-charcoal-600">
                <input type="checkbox" checked={!!r.active} onChange={(e) => setRows(rows.map((x) => x.id === r.id ? { ...x, active: e.target.checked } : x))} /> active
              </label>
              <button data-testid={`kb-save-${r.id}`} onClick={() => saveRow(r)} className="rounded-full bg-sage px-3.5 py-1.5 text-xs font-medium text-white hover:bg-sage-hover">Save</button>
              <button onClick={() => del(r.id)} className="p-1 text-charcoal-400 hover:text-destructive"><Trash2 size={14} /></button>
            </div>
          </div>
          <textarea rows={4} className={`${FIELD} mt-2 text-xs`} value={r.content}
            onChange={(e) => setRows(rows.map((x) => x.id === r.id ? { ...x, content: e.target.value } : x))} />
        </Card>
      ))}
    </div>
  );
}

function InsightsTab() {
  const [data, setData] = useState(null);
  const [openGap, setOpenGap] = useState(null);
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/chat-insights").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-sm text-charcoal-500">Loading…</p>;

  const startAnswer = (g) => { setOpenGap(g.id === openGap ? null : g.id); setAnswer(""); };
  const saveAnswer = async (g) => {
    if (!answer.trim()) return toast.error("Write the approved answer first.");
    setSaving(true);
    try {
      await api.post("/admin/chat-kb", { title: g.question.slice(0, 200), content: answer, priority: 1 });
      await api.delete(`/admin/chat-gaps/${g.id}`).catch(() => {});
      toast.success("Answer added to the knowledge base — the AI can use it immediately.");
      setOpenGap(null); setAnswer("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };
  const dismissGap = async (g) => {
    try { await api.delete(`/admin/chat-gaps/${g.id}`); load(); }
    catch { toast.error("Could not dismiss"); }
  };

  const tiles = [
    ["total_chats", "Total chats"], ["ai_resolved", "AI-resolved (no human)"],
    ["escalated", "Human handoffs"], ["low_confidence", "Low-confidence answers"],
  ];
  return (
    <div className="space-y-6" data-testid="chat-insights">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map(([k, label]) => (
          <Card key={k}>
            <p className="font-serif text-3xl text-charcoal-800" data-testid={`insight-${k}`}>{data[k]}</p>
            <p className="text-sm text-charcoal-500 mt-1">{label}</p>
          </Card>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h3 className="font-medium text-charcoal-800 mb-3">Top questions (recent chats)</h3>
          {data.top_questions.length === 0 ? <p className="text-sm text-charcoal-500">No chats yet.</p> : (
            <div className="space-y-2">
              {data.top_questions.map((q, i) => (
                <div key={i} className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-charcoal-700">{q.question}</span>
                  <span className="shrink-0 rounded-full bg-sand-200 px-2 py-0.5 text-xs text-charcoal-600">×{q.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <h3 className="font-medium text-charcoal-800 mb-1">Knowledge gaps</h3>
          <p className="text-xs text-charcoal-500 mb-3">Questions the AI couldn't answer confidently. Add approved answers in the Knowledge tab to close the gaps.</p>
          {data.knowledge_gaps.length === 0 ? <p className="text-sm text-charcoal-500">No gaps recorded — the AI is answering everything it's asked. 🎉</p> : (
            <div className="space-y-3">
              {data.knowledge_gaps.map((g) => (
                <div key={g.id} className="rounded-xl bg-sand-100 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-charcoal-800">{g.question}</p>
                      <p className="text-xs text-charcoal-500 mt-0.5">{g.reason} · {g.created_at?.slice(0, 10)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid={`gap-answer-${g.id}`} onClick={() => startAnswer(g)}
                        className="rounded-full bg-terracotta px-3 py-1 text-xs font-medium text-white hover:bg-terracotta-hover">
                        {openGap === g.id ? "Cancel" : "Add answer"}
                      </button>
                      <button data-testid={`gap-dismiss-${g.id}`} onClick={() => dismissGap(g)} aria-label="Dismiss gap"
                        className="p-1 text-charcoal-400 hover:text-red-600"><Trash2 size={13} /></button>
                    </div>
                  </div>
                  {openGap === g.id && (
                    <div className="mt-3 space-y-2">
                      <textarea data-testid={`gap-answer-input-${g.id}`} rows={3} className={FIELD}
                        placeholder="Write the approved answer the AI should give for this question…"
                        value={answer} onChange={(e) => setAnswer(e.target.value)} />
                      <button data-testid={`gap-answer-save-${g.id}`} onClick={() => saveAnswer(g)} disabled={saving}
                        className="rounded-full bg-sage px-4 py-1.5 text-xs font-medium text-white hover:bg-sage-hover disabled:opacity-60">
                        {saving ? "Saving…" : "Save to knowledge base"}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function ConversationsTab({ employees }) {
  const [chats, setChats] = useState([]);
  const [filter, setFilter] = useState("all");
  const [activeId, setActiveId] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = (retry = true) => api.get("/admin/chats").then((r) => setChats(r.data)).catch(() => {
    if (retry) setTimeout(() => load(false), 2000);
    else toast.error("Could not load chats — retrying shortly.");
  });
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const list = chats.filter((c) => filter === "all" ? true : filter === "escalated" ? c.escalated : c.status === filter);
  const active = chats.find((c) => c.id === activeId);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [activeId, chats]);

  const openChat = async (c) => {
    setActiveId(c.id);
    if (c.unread_admin) {
      try { await api.patch(`/admin/chats/${c.id}`, { unread_admin: false }); load(); } catch { /* noop */ }
    }
  };
  const patch = async (body, okMsg) => {
    try { await api.patch(`/admin/chats/${active.id}`, body); if (okMsg) toast.success(okMsg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/admin/chats/${active.id}/reply`, { message: reply.trim() });
      setReply("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-1 max-h-[72vh] overflow-y-auto">
        <div className="mb-3 flex flex-wrap gap-1.5">
          {["all", "escalated", "open", "pending", "resolved"].map((f) => (
            <button key={f} data-testid={`chat-filter-${f}`} onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1 text-xs capitalize ${filter === f ? "bg-charcoal-800 text-white" : "bg-sand-100 text-charcoal-600 hover:bg-sand-200"}`}>
              {f}{f === "escalated" && chats.some((c) => c.escalated && c.status !== "resolved") ? " •" : ""}
            </button>
          ))}
        </div>
        {list.length === 0 && <p className="text-sm text-charcoal-500">No conversations here.</p>}
        <div className="space-y-1.5">
          {list.map((c) => (
            <button key={c.id} data-testid={`chat-row-${c.id}`} onClick={() => openChat(c)}
              className={`w-full rounded-xl px-3.5 py-2.5 text-left transition-colors ${activeId === c.id ? "bg-sage-light" : "hover:bg-sand-100"}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-medium text-charcoal-800">{c.name}</p>
                <div className="flex shrink-0 items-center gap-1.5">
                  {c.unread_admin && <span data-testid={`chat-unread-${c.id}`} className="h-2 w-2 rounded-full bg-terracotta" />}
                  {c.escalated && <UserRound size={12} className="text-clay-800" title="Escalated to human" />}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${STATUS_COLORS[c.status] || STATUS_COLORS.open}`}>{c.status === "closed" ? "resolved" : c.status}</span>
                </div>
              </div>
              <p className="truncate text-xs text-charcoal-500">{c.service || "general"} · {fmt(c.last_message_at)}{c.assigned_employee_name ? ` · ${c.assigned_employee_name}` : ""}</p>
              <p className="truncate text-xs text-charcoal-400">{(c.messages || []).slice(-1)[0]?.text}</p>
            </button>
          ))}
        </div>
      </Card>

      <div className="lg:col-span-2">
        {!active ? (
          <Card><p className="text-sm text-charcoal-500">Select a conversation.</p></Card>
        ) : (
          <Card className="flex max-h-[72vh] flex-col">
            <div className="mb-3 space-y-2 border-b border-sand-100 pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-charcoal-800">{active.name} <span className="text-sm text-charcoal-500">· {active.email}{active.phone ? ` · ${active.phone}` : ""}</span></p>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-charcoal-500">
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {fmt(active.created_at)}</span>
                    <span className="inline-flex items-center gap-1" title="Approximate (IP-based)"><Globe size={11} /> {locLine(active.meta)} <em className="text-charcoal-400">(approx.)</em></span>
                    {active.meta?.geo_consent && active.meta?.precise_location && (
                      <a className="inline-flex items-center gap-1 text-sage-700 underline" target="_blank" rel="noreferrer"
                        href={`https://www.google.com/maps?q=${active.meta.precise_location.lat},${active.meta.precise_location.lon}`}>
                        <MapPin size={11} /> precise location (consented)
                      </a>
                    )}
                    {active.meta?.page_url && <span className="truncate max-w-[260px]">Page: {active.meta.page_url}</span>}
                  </div>
                  {(active.pets || []).length > 0 && (
                    <div data-testid="chat-admin-pets" className="mt-2 flex flex-wrap gap-1.5">
                      {active.pets.map((p, i) => (
                        <span key={i} className="inline-flex items-center gap-1 rounded-full bg-sand-100 border border-sand-200 px-2.5 py-0.5 text-[11px] font-medium text-charcoal-700"
                          title={p.temperament || ""}>
                          🐾 {p.name || "Pet"}{p.species ? ` · ${p.species}` : ""}{p.temperament ? ` — ${p.temperament}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select data-testid="chat-status-select" className={`${FIELD} w-32`} value={active.status === "closed" ? "resolved" : active.status}
                    onChange={(e) => patch({ status: e.target.value }, `Status: ${e.target.value}`)}>
                    {["open", "pending", "resolved"].map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                  <select data-testid="chat-assign-select" className={`${FIELD} w-40`} value={active.assigned_employee_id || ""}
                    onChange={(e) => patch({ assigned_employee_id: e.target.value }, "Assignment updated")}>
                    <option value="">Unassigned</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
              </div>
              {active.escalated && active.escalation && (
                <div data-testid="chat-escalation-banner" className="rounded-xl bg-clay-100 px-3.5 py-2.5 text-xs text-clay-800">
                  <p className="font-medium">Escalated by {active.escalation.by} — {active.escalation.reason} {active.escalation.humans_online === false && "(team was offline; visitor told we'd follow up)"}</p>
                  {active.escalation.summary && <p className="mt-1"><Sparkles size={11} className="mr-1 inline" /><b>AI summary:</b> {active.escalation.summary}</p>}
                </div>
              )}
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto rounded-xl bg-sand-50 p-3">
              {(active.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.from === "visitor" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap
                    ${m.from === "visitor" ? "bg-white border border-sand-200 text-charcoal-800"
                      : m.from === "admin" ? "bg-charcoal-800 text-white"
                        : m.from === "bot" ? "bg-sage-light text-charcoal-800" : "bg-sand-200 text-charcoal-600"}`}>
                    <p className="mb-0.5 text-[10px] opacity-60">
                      {m.from === "visitor" ? active.name : m.from === "admin" ? (m.by || "admin") : m.from === "bot" ? "AI assistant" : "system"} · {fmt(m.at)}
                    </p>
                    {m.text}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <form onSubmit={sendReply} className="mt-3 flex items-center gap-2">
              <input data-testid="chat-reply-input" className={FIELD} placeholder="Reply as the team (this pauses the AI for this chat)…" value={reply} onChange={(e) => setReply(e.target.value)} aria-label="Reply" />
              <button data-testid="chat-reply-send" type="submit" disabled={sending}
                className="shrink-0 rounded-full bg-sage p-2.5 text-white hover:bg-sage-hover disabled:opacity-60"><Send size={16} /></button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function ChatManager() {
  const [tab, setTab] = useState("conversations");
  const [employees, setEmployees] = useState([]);
  useEffect(() => { api.get("/admin/employees").then((r) => setEmployees(r.data || [])).catch(() => setEmployees([])); }, []);

  return (
    <div data-testid="admin-chats">
      <SectionTitle title="Chat" subtitle="AI answers from your approved site knowledge first; humans take over any time. Every chat is a lead." />
      <div className="mb-6 flex gap-2">
        {[["conversations", "Conversations"], ["settings", "AI & handoff settings"], ["knowledge", "Knowledge base"], ["insights", "Insights"]].map(([id, label]) => (
          <button key={id} data-testid={`chat-tab-${id}`} onClick={() => setTab(id)}
            className={`rounded-full px-4 py-2 text-sm font-medium ${tab === id ? "bg-charcoal-800 text-white" : "bg-sand-100 text-charcoal-600 hover:bg-sand-200"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "conversations" && <ConversationsTab employees={employees} />}
      {tab === "settings" && <SettingsTab employees={employees} />}
      {tab === "knowledge" && <KnowledgeTab />}
      {tab === "insights" && <InsightsTab />}
    </div>
  );
}
